import React, { FC } from 'react';
import { Avatar, Box, Link, Paper, Stack, Typography } from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useTranslation } from 'react-i18next';
import { AssistantChatMessage } from 'models/assistant';
import AssistantMessageText from './assistant-message-text.component';
import { useLineReveal } from './use-line-reveal';
import AssistantProblemReport from './assistant-problem-report.component';

type AssistantMessageBubbleProps = {
    message: AssistantChatMessage;
    /** Show the text line by line (the newest answer, which arrived in one piece). */
    reveal?: boolean;
    onArticleClicked: (articleId: string) => void;
    onNavigate: (path: string) => void;
    onShowUiElement: (name: string) => void;
    onSendProblemReport: (report: string) => Promise<boolean>;
};

const AssistantMessageBubble: FC<AssistantMessageBubbleProps> = ({
    message,
    reveal = false,
    onArticleClicked,
    onNavigate,
    onShowUiElement,
    onSendProblemReport,
}: AssistantMessageBubbleProps) => {
    const { t: translate } = useTranslation(['help-drawer']);
    const visibleText = useLineReveal(message.text, reveal);
    const isRevealed = visibleText.length === message.text.length;
    const isUser = message.role === 'USER';
    const problemReportDraft = message.problemReportDraft;
    return (
        <Box display="flex" justifyContent={isUser ? 'flex-end' : 'flex-start'} alignItems="flex-end" gap={1}>
            {isUser ? undefined : (
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
                    <SupportAgentIcon sx={{ fontSize: 18 }} />
                </Avatar>
            )}
            <Paper
                elevation={0}
                sx={{
                    px: 2,
                    py: 1.25,
                    maxWidth: '80%',
                    // Rounded, with a small "tail" corner on the speaker's side.
                    borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    bgcolor: isUser ? 'primary.main' : 'background.paper',
                    color: isUser ? 'primary.contrastText' : 'text.primary',
                    border: isUser ? 0 : 1,
                    borderColor: 'divider',
                    boxShadow: isUser ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.06)',
                }}
            >
                {isUser ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {message.text}
                    </Typography>
                ) : (
                    <AssistantMessageText
                        text={visibleText}
                        onArticleClicked={onArticleClicked}
                        onNavigate={onNavigate}
                        onShowUiElement={onShowUiElement}
                    />
                )}
                {isRevealed && message.articleLinks?.length ? (
                    <Stack mt={1} spacing={0.5} alignItems="flex-start">
                        <Typography variant="caption" color="grey">
                            {translate('help-drawer:ASSISTANT_SOURCES')}
                        </Typography>
                        {message.articleLinks.map((article) => (
                            <Link
                                key={article.articleId}
                                component="button"
                                variant="body2"
                                textAlign="left"
                                onClick={() => onArticleClicked(article.articleId)}
                            >
                                {article.title}
                            </Link>
                        ))}
                    </Stack>
                ) : undefined}
                {isRevealed && problemReportDraft ? (
                    <AssistantProblemReport draft={problemReportDraft} onSend={onSendProblemReport} />
                ) : undefined}
            </Paper>
        </Box>
    );
};

export default AssistantMessageBubble;
